
import { Component, Vue } from 'vue-property-decorator';
import { Mutation } from 'vuex-class';
import Price from '@/components/price/index.vue';
import Header from '@/common/header.vue';
import Footer from '@/common/footer.vue';
import NavMenu from '@/common/navmenu.vue';
import {
  orderDetail,
  companyPayInfo,
  submitCompanyPay,
  createOrderAgain,
  refundListDetail,
  orderReceived,
  pageVisit,
  orderCancel,
  getDataOrder
} from '@/services/http';
import orderRequestAnchorSrc from '@/tools/orderRequestAnchorSrc';
import { dateTimeFormatter, readCookie, writeStorage } from '@/tools/tools';
import CountDown from '@/components/countdown/index.vue';
import BackTop from '@/common/backtop.vue';
import TransferDialog from '@/components/transfer-dialog/index.vue';
import * as _ from 'lodash';

@Component({
  name: 'OrderDetails',
  components: {
    Header,
    Price,
    Footer,
    NavMenu,
    CountDown,
    BackTop,
    TransferDialog
  },
  filters: {
    dateTimeFormatter(value: number) {
      return dateTimeFormatter(value);
    }
  }
})
export default class extends Vue {
  detailsList: any = {} // 商品详情信息

  /** 付款明细（优惠项）折叠，默认收起对齐设计稿 */
  paymentDetailExpanded: boolean = false

  transferDialogVisible: boolean = false

  transferDialogStatus: number = 1

  createTime: string = '' // 下单时间

  updateTime: string = '' // 更新时间

  isLoading: boolean = false // loading加载

  paytime: number = 0

  // 售后列表
  mallOrderRefundList: any[] = []

  tableData: any[] = []

  giftItemCount: number = 0

  transferPayAmount: string = ''

  transferPayeeName: string = ''

  transferPayeeNo: string = ''

  transferPayeeBank: string = ''

  transferPayerName: string = ''

  transferVoucherUrl: string = ''

  get transferAccountName(): string {
    return this.detailsList.payeeAccountName
      || this.detailsList.transferAccountName
      || this.detailsList.payAccountName
      || this.detailsList.paymentAccountName
      || '';
  }

  get transferDeadline(): string {
    if (this.detailsList.deadline) {
      return dateTimeFormatter(this.detailsList.deadline);
    }
    if (this.detailsList.createTime) {
      return dateTimeFormatter(this.detailsList.createTime);
    }
    return '';
  }

  get isCompanyPayOrder(): boolean {
    return Number(this.detailsList.payType) === 3
      || Number(this.detailsList.orderType) === 6
      || [1, 2, 3, 4].includes(Number(this.detailsList.companyCheckStatus));
  }

  get paymentTimeText(): string {
    if (this.detailsList.payTime) return dateTimeFormatter(this.detailsList.payTime);
    const surplus = Number(this.detailsList.surplus);
    if (this.detailsList.orderStatus === 0 && Number.isFinite(surplus) && surplus > 0) {
      return dateTimeFormatter(Date.now() + surplus * 1000);
    }
    return '-';
  }

  get shipmentList(): any[] {
    if (this.detailsList.logisticsList && this.detailsList.logisticsList.length) {
      return this.detailsList.logisticsList;
    }
    if (this.detailsList.logisticsNo) {
      return [{
        id: 'default-shipment',
        label: '包裹1',
        company: this.detailsList.companyLogistics
          ? this.detailsList.companyLogistics.logisticsName : '',
        number: this.detailsList.logisticsNo
      }];
    }
    return [];
  }

  // 改价优惠由后端直接下发（adjustmentDiscountAmount），可正可负，0 表示无改价
  get priceAdjustmentAmount(): number {
    const amount = Number(this.detailsList.adjustmentDiscountAmount || 0);
    if (!Number.isFinite(amount) || amount === 0) return 0;
    return -amount;
  }

  get paymentDiscountItems() {
    const data = this.detailsList;
    const items = [
      { key: 'coupon', label: '优惠券', amount: -Number(data.couponAmount || 0) },
      { key: 'member', label: '会员等级折扣', amount: -Number(data.discountAmount || 0) },
      { key: 'marketing', label: '单件/满件折扣', amount: -Number(data.marketingDiscountAmount || 0) },
      { key: 'integral', label: '积分抵现', amount: -Number(data.integralAmount || 0) },
      { key: 'exchange', label: `使用${data.exchangeIntegral}积分兑换`, amount: -Number(data.exchangeAmount || 0) }
    ];
    // 待付款态改价优惠独立一行展示，不计入共减折叠明细
    if (data.orderStatus !== 0) {
      items.push({ key: 'adjustment', label: '改价优惠', amount: this.priceAdjustmentAmount });
    }
    return items.filter((item) => Number.isFinite(item.amount) && item.amount !== 0);
  }

  get totalDiscountAmount(): string {
    const amount = _.round(_.sumBy(this.paymentDiscountItems, 'amount'), 2);
    return `${amount > 0 ? '+' : ''}${amount.toFixed(2)}`;
  }

  @Mutation('card/SETCARDLIST') SETCARDLIST!: Function

  created() {
    this.getOrderDetail();
    this.pageVisit();
  }

  // 确认订单列表
  private async getOrderDetail() {
    this.isLoading = true;
    const anchor = readCookie('anchor');
    const res: any = await orderDetail({
      orderNo: this.$route.query.id,
      src: orderRequestAnchorSrc(anchor)
    });
    if (res.data.code === 100) {
      const data = res.data.data || {};
      if (Array.isArray(data.packageList)) {
        data.logisticsList = data.packageList.map((item: any, index: number) => ({
          id: item.id || `package-${index + 1}`,
          label: `包裹${index + 1}`,
          company: item.logisticsCompany || '',
          number: item.logisticsNo || ''
        })).filter((item: any) => item.number);
      }
      if (this.isCompanyPayData(data)) {
        const payRes: any = await companyPayInfo({ orderNo: data.orderNo });
        if (payRes.data.code === 100 && Array.isArray(payRes.data.data) && payRes.data.data.length) {
          const [payInfo] = payRes.data.data;
          Object.assign(data, {
            payAmount: payInfo.payAmount,
            payeeAccountName: payInfo.payeeAccountName,
            payeeBankNo: payInfo.payeeBankNo,
            payeeBank: payInfo.payeeBank,
            payerName: payInfo.payerName || data.payerName,
            voucherUrl: payInfo.voucherUrl,
            payTime: payInfo.payTime || data.payTime,
            companyCheckStatus: payInfo.companyCheckStatus
          });
        }
      }
      this.setDetailsData(data);
      // this.paytime = countDown(Number(res.data.data.surplus));
      this.isLoading = false;
    }
  }

  private setDetailsData(data: any) {
    const flatList: any[] = [];
    let giftCount = 0;
    data.orderDetailList.forEach((value: any) => {
      this.$set(value, 'discount_price', _.round(_.subtract(value.price, value.flashAmount), 2));
      flatList.push(value);
      if (value.giftMallImage) {
        giftCount += 1;
        flatList.push({
          _isGift: true,
          _imgSrc: value.giftMallImage.cdn_file_path_pc,
          _name: value.giftMallImage.product_name,
          _natureValue: value.giftMallImage.natureValue,
          _price: value.giftMallImage.price ? value.giftMallImage.price.price : 0,
          _num: value.giftMallImage.num
        });
      }
    });
    this.tableData = flatList;
    this.giftItemCount = giftCount;
    this.detailsList = data;
    this.createTime = dateTimeFormatter(data.createTime);
    this.updateTime = dateTimeFormatter(data.updateTime);
  }

  async openTransferDialog() {
    const status = Number(this.detailsList.companyCheckStatus);
    this.transferDialogStatus = status === 4 ? 1 : (status || 1);
    this.transferPayAmount = String(this.detailsList.totalAmount || '');
    this.transferPayeeName = '';
    this.transferPayeeNo = '';
    this.transferPayeeBank = '';
    this.transferPayerName = this.detailsList.payerName || '';
    this.transferVoucherUrl = this.detailsList.voucherUrl || '';
    const res: any = await companyPayInfo({ orderNo: this.detailsList.orderNo });
    if (res.data.code === 100 && Array.isArray(res.data.data) && res.data.data.length) {
      const [info] = res.data.data;
      this.transferPayAmount = String(info.payAmount || this.transferPayAmount);
      this.transferPayeeName = info.payeeAccountName || '';
      this.transferPayeeNo = info.payeeBankNo || '';
      this.transferPayeeBank = info.payeeBank || '';
      this.transferPayerName = info.payerName || this.transferPayerName;
      this.transferVoucherUrl = info.voucherUrl || this.transferVoucherUrl;
      const companyCheckStatus = Number(info.companyCheckStatus);
      this.transferDialogStatus = companyCheckStatus === 4 ? 1 : (companyCheckStatus || this.transferDialogStatus);
    }
    this.transferDialogVisible = true;
  }

  private isCompanyPayData(data: any): boolean {
    return Number(data.payType) === 3 || Number(data.orderType) === 6
      || [1, 2, 3, 4].includes(Number(data.companyCheckStatus));
  }

  async handleTransferSubmit(payload: any) {
    let success = false;
    try {
      const res: any = await submitCompanyPay({
        orderNo: payload.orderNo || this.detailsList.orderNo,
        payerName: payload.voucherName,
        voucherUrl: payload.voucherUrl
      });
      success = res.data.code === 100;
      if (success) {
        this.detailsList.payerName = payload.voucherName;
        this.detailsList.voucherUrl = payload.voucherUrl;
        this.detailsList.companyCheckStatus = 2;
        this.transferPayerName = payload.voucherName;
        this.transferVoucherUrl = payload.voucherUrl;
        this.transferDialogStatus = 2;
        this.$message.success('提交成功');
      } else {
        this.$message.error(res.data.message);
      }
    } finally {
      payload.complete(success);
    }
  }

  handleTransferClose() {
    this.transferDialogVisible = false;
  }

  goLogistics() {
    this.$router.push({
      name: 'logistics',
      query: {
        packages: String(this.shipmentList.length || 1),
        id: this.detailsList.orderNo
      }
    });
  }

  private addAmount(val: number, num: number) {
    return _.round(_.multiply(val, num), 2);
  }

  // 确认收货
  private async confirmReceived(item: any) {
    for (let i = 0; i < item.orderDetailList.length; i += 1) {
      if (item.orderDetailList[i].refundStatus === 1) {
        this.$message.warning('该订单售后中，不可确认收货!');
        return;
      }
    }
    this.$confirm('您是否确认收货?', '提示', {
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      type: 'warning'
    })
      .then(async () => {
        const res: any = await orderReceived({ orderNo: item.orderNo });
        if (res.data.code === 100) {
          const integral = res.data.data.integral > 0 ? 'bolck' : 'none';
          const growth = res.data.data.growthValue > 0 ? 'bolck' : 'none';
          this.$message.success({
            showClose: true,
            dangerouslyUseHTMLString: true,
            message: `
            <p style="margin-bottom: 8px; color: #333; font-size: 14px;">收货成功</p>
            <span style="display: ${integral}; color: #999; font-size: 12px;">奖励积分+${res.data.data.integral}；</span>
            <span style="display: ${growth}; color: #999; font-size: 12px;">奖励成长值+${res.data.data.growthValue}</span>
          `
          });
          this.getOrderDetail();
        } else {
          this.$message.error(res.data.message);
        }
      })
      .catch(() => {});
  }

  // 再次购买
  private async buyAgain(status: number) {
    const anchor = readCookie('anchor');
    const res: any = await createOrderAgain({
      orderNo: this.$route.query.id,
      equip: anchor ? anchor.equip : 4,
      version: anchor ? anchor.version : ''
    });
    if (res.data.code === 100) {
      await getDataOrder({
        orderNum: '',
        orderStatus: status,
        refundReason: '',
        equip: anchor ? anchor.equip : 4,
        version: anchor ? anchor.version : '',
        src: orderRequestAnchorSrc(anchor),
        model: anchor ? anchor.model : '',
        equipVersion: anchor ? anchor.equipVersion : '',
        equipMeid: anchor ? anchor.equipMeid : '',
        eventNo: this.$route.query.from ? '3.1.1.3.1.0.1' : '3.2.1.4.1.0.1',
        companyId: 1
      });
      this.$router.push('/card');
    } else {
      this.$message.error(res.data.message);
    }
  }

  //  申请售后
  private applyscale(element: any, id: number) {
    // 检查是否为对公打款订单
    if (this.isCompanyPayOrder) {
      this.$msgbox({
        message: '此订单为对公打款支付，如需退款，请直接联系「在线客服」为您处理。',
        title: '提示',
        confirmButtonText: '知道了',
        confirmButtonClass: 'transfer-refund-confirm',
        type: 'warning',
        showClose: true,
        customClass: 'transfer-refund-alert'
      });
      return;
    }

    if (element.canRefund) {
      const obj = [
        {
          id: element.id,
          buyNum: element.buyNum,
          price: element.price,
          product_name: element.productName,
          norms: element.natureList,
          orderNo: element.orderNo,
          mallImage: element.mallImage.sourceUrl,
          totalPrice: element.totalPrice

        }
      ];
      writeStorage('exchangeList', obj);
      this.$router.push(
        this.$route.query.from
          ? `/exchange?id=${element.id}&sendid=${id}&from=center&orderNo=${this.$route.query.id}`
          : `/exchange?id=${element.id}&sendid=${id}&orderNo=${this.$route.query.id}`
      );
    }
  }

  // 获取售后申请列表
  private async toogleExpand(row: any) {
    const res: any = await refundListDetail({
      orderDetailId: row.id
    });
    this.mallOrderRefundList = res.data.data.mallOrderRefundList;
    (this.$refs as any).table.toggleRowExpansion(row);
  }

  private applyDetails(id: string) {
    this.$router.push(`/exchange-details?id=${id}`);
  }

  // 取消订单
  private async cancelOrder() {
    const res: any = await orderCancel({
      orderNo: this.detailsList.orderNo
    });
    if (res.data.code === 100) {
      this.$message.success('取消成功！');
      this.$router.push('/order-list');
    }
  }

  private goPay() {
    const anchor = readCookie('anchor');
    getDataOrder({
      orderNum: '',
      orderStatus: 0,
      refundReason: '',
      equip: anchor ? anchor.equip : 4,
      version: anchor ? anchor.version : '',
      src: orderRequestAnchorSrc(anchor),
      model: anchor ? anchor.model : '',
      equipVersion: anchor ? anchor.equipVersion : '',
      equipMeid: anchor ? anchor.equipMeid : '',
      eventNo: this.$route.query.from ? '3.1.1.1.1.0.1' : '3.2.1.2.1.0.1',
      companyId: 1
    });
    this.$router.push(`/payment?id=${this.$route.query.id}`);
  }

  //  点击商品到详情
  godetails(item: any) {
    const obj = item;
    obj.num = item.buyNum;
    this.SETCARDLIST([obj]);
    this.$router.push({
      path: `/details?id=${item.productId}`,
      query: {
        productPriceId: item.productPriceId,
        shareUserId: ''
      }
    });
  }

  // 评价
  private async comment(details: object) {
    sessionStorage.setItem('commentDetails', JSON.stringify(details));
    (this as any).$router.push({ name: 'Comment' });
  }

  // 复制成功
  private onCopy() {
    this.$message.success('复制成功！');
  }

  // 复制失败
  private onError() {
    this.$message.error('该浏览器不支持自动复制,请手动复制！');
  }

  // 访问页面数据埋点
  private pageVisit() {
    const anchor = readCookie('anchor');
    pageVisit({
      pageType: 8,
      equip: anchor ? anchor.equip : 4,
      version: anchor ? anchor.version : '',
      src: orderRequestAnchorSrc(anchor),
      model: anchor ? anchor.model : '',
      equipVersion: anchor ? anchor.equipVersion : '',
      equipMeid: anchor ? anchor.equipMeid : '',
      companyId: 1
    });
  }
}
